<template>
	<div :key="downloadsCounter" class="relative">
		<div v-if="showCalcButton" class="m-4 text-left pl-4 pb-4">
			<div class="text-red-600 font-bold mb-4" v-if="repairExtension.id != null && !repairExtension.price">
				{{ $t('please_enter_price_and_set_signs') }}
			</div>
			<div class="flex space-x-4">
				<DocumentFileUploader
					ref="DocumentFileUploaderPdf"
					:instant-upload="false"
					class="cursor-pointer mt-8"
					:handle-on-click="false"
					:document="documentPdf"
					accept="application/pdf"
					:repair-extension-id="repairExtension.id"
					@input="updateDocumentPdf"
					@update:document="sendSignableDocToApi($event)"
				>
					<BaseButton @click="askForPDFOverwrite">
						{{ $t('upload_repair_extension_pdf', { index: realIndex }) }}
						<font-awesome-icon class="text-green-300" :icon="['fas', 'check']" v-if="repairExtension.pdf != null"></font-awesome-icon>
					</BaseButton>
				</DocumentFileUploader>
				<div class="w-1/4" @click="pauseEventListener = true">
					<BaseInputNumber
						v-model="repairExtension.price"
						ref="baseInputPrice"
						:precision="2"
						:label="$t('price_repair_extension')"
						:required="true"
					/>
				</div>
			</div>
			<div>
				<div v-if="documentData != null">
					<BaseButton @click="pushSignLocation" class="mt-4 mb-4">
						<font-awesome-icon :icon="['fas', 'expand']" class="text-gray-700 mr-2" />
						{{ $t('add_sign') }}
					</BaseButton>
				</div>
				<VuePdfEmbed ref="myPdfComponent" :source="documentData" style="display: none" v-if="documentData != null" />
				<div style="align-items: start;" class="flex flex-wrap gap-8" v-if="pdfPageCount > 0">
					<div v-for="page in pdfPageCount" :key="page" style="width: 553px; height: 780px;">
						<div
							class="shadow-md mr-6 border"
							style="align-items: start; position: absolute;  display: flex; border-width: 3px"
							:style="selectedPage === page ? 'border-color: #0068B4' : 'border-color: #e2e8f0'"
							@click="
								selectedPage = page;
								pauseEventListener = false;
							"
						>
							<VuePdfEmbed :page="page" height="780" width="553" :source="documentData"></VuePdfEmbed>
							<div style="position: absolute; height: 780px; width: 553px" @click="selectedSign = null">
								<vue-draggable-resizable
									v-for="signLocation in getSignsForPage(page)"
									v-model="dragElement"
									:handles="['tl', 'tr', 'bl', 'br']"
									:w="200"
									:h="40"
									:min-width="200"
									:min-height="40"
									:parent="true"
									:max-width="400"
									:max-height="100"
									:key="signLocation.id"
									class="cursor-pointer"
									@start="drag = true"
									@resizing="(x, y, eWidth, eHeight) => onResize(x, y, eWidth, eHeight, signLocation.id)"
									@dragstop="(x, y) => onDragStop(x, y, signLocation.id)"
								>
									<div
										class="border-4 justify-center italic"
										@click.stop="selectedSign = signLocation.id"
										:style="`width: ${signLocation.width}px; height: ${signLocation.height}px`"
										style="border-color: red; min-height: 40px; min-width: 200px; text-align: center; max-height: 100px; max-width: 400px;"
									>
										{{ $t('sign') }}
									</div>
								</vue-draggable-resizable>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="text-left mt-4 ml-8 flex justify-between mr-8 items-center">
			<h3 class="font-bold text-lg" v-if="repairExtension.isNew">{{ getStringFromDate(repairExtension.date) }}</h3>
			<div class="font-bold text-lg" v-if="!repairExtension.isNew">
				{{ getStringFromDate(repairExtension.date) }} - {{ getTimeFromDate(repairExtension.date) }}
			</div>

			<div class="flex">
				<div class="font-bold text-lg mr-4">{{ $t('repair_extension_index', { index: realIndex }) }}</div>

				<div v-if="isRepairExtensionApproved && isUserAllowedInLevel5or6" class="text-green-300">
					<font-awesome-icon :icon="['fas', 'thumbs-up']"></font-awesome-icon>
				</div>
				<div v-if="isRepairExtensionRejected && isUserAllowedInLevel5or6" class="text-red-300">
					<font-awesome-icon :icon="['fas', 'thumbs-down']"></font-awesome-icon>
				</div>
				<div v-if="!isRepairExtensionRejected && !isRepairExtensionApproved && isUserAllowedInLevel5or6">
					<font-awesome-icon :icon="['fas', 'question']"></font-awesome-icon>
				</div>
			</div>
		</div>

		<div class="mx-8 border-2 mt-4"></div>

		<div class="text-left mt-8 ml-8 flex justify-between flex-wrap flex-col md:flex-row mb-4">
			<div class="mr-8 w-48 flex-grow mb-4 md:mb-0">
				<h3 class="font-bold">{{ $t('images') }}</h3>
				<div v-if="!repairExtension.imageFiles || !repairExtension.imageFiles.length">
					{{ $t('no_images_uploaded') }}
				</div>
			</div>
		</div>

		<BaseCamera
			v-if="isUserAllowedInLevel5or6 && isRepairExtensionRequested && !isExtensionClosed"
			class="ml-8 mt-4 text-left mr-8"
			:disable-success-message="true"
			:enable-preview="false"
			:allow-upload-in-app-container="false"
			@snapshot="addImageFile($event, repairExtension)"
		/>

		<div class="mt-4 ml-8 mr-8" v-if="repairExtension.imageFiles">
			<div class="grid grid-cols-2 xl:grid-cols-3 gap-8">
				<div v-for="(metaImage, key) in repairExtension.imageFiles" :key="metaImage.id">
					<MetaInfoImage
						:meta-info-media="metaImage"
						v-model="metaImage.isSelected"
						@delete="removeImageFile(key)"
						@input="checkIsFileSelected"
						:chooseable="!isUserAllowedInLevel5or6"
						:deletable="isUserAllowedInLevel5or6 && isRepairExtensionRequested && !isExtensionClosed"
					/>
				</div>
			</div>
		</div>

		<div class="mx-8 border-2 mt-8"></div>

		<div class="text-left m-8">
			<BaseInput :label="$t('description_input')">
				<textarea
					class="resize-y w-full shadow-m h-32"
					v-model="repairExtension.description"
					:disabled="!isUserAllowedInLevel5or6 || !isRepairExtensionRequested || isExtensionClosed"
				></textarea>
			</BaseInput>
		</div>

		<div class="mx-8 border-2 mt-8" v-if="repairExtension.damageAssessorComment"></div>

		<div class="text-left m-8" v-if="repairExtension.damageAssessorComment">
			<BaseInput ref="damageAssessorComment" :label="$t('damage_assessor_notes_input')" :error-message="$t('explain_decision')">
				<textarea
					class="resize-y w-full shadow-m h-32"
					v-model="repairExtension.damageAssessorComment"
					:disabled="isUserAllowedInLevel5or6"
				></textarea>
			</BaseInput>
		</div>

		<div class="flex justify-end">
			<div class="text-right mx-8 mt-8" v-if="isUserAllowedInLevel5or6 && !isExtensionClosed && isAppContainer">
				<BaseButton @click="save(true)" class="btn-blue">{{ getSaveButtonText }}</BaseButton>
			</div>
			<div class="text-right mx-8 mt-8" v-if="isUserAllowedInLevel5or6 && isEditable && !isAppContainer && repairExtension.pdf_created_at == null">
				<BaseButton @click="updateExtension" class="btn-blue">{{ getSaveButtonText }}</BaseButton>
			</div>
		</div>
	</div>
</template>

<script>
import apiDefaults from '@/api/defaultConfiguration';
import { getStringFromDate, getTimeFromDate, getUnixTimestamp } from '@/plugins/Utils';
import { v4 } from 'uuid';
import { DamagecasesApiFactory, MetainfofileApiFactory } from '@/api/generated';
import MetaInfoImage from '@/components/damagecases/forms/MetaInfoImage';
import BaseButton from '@/components/forms/buttons/BaseButton';
import BaseCamera from '@/components/forms/inputs/BaseCamera';
import BaseInput from '@/components/forms/inputs/BaseInput';
import LoadingSpinner from '@/mixins/LoadingSpinner';
import Authentication from '@/mixins/Authentication';
import { MetaInfoFileTypeEnum, RepairExtensionStatusEnum } from '@/api/generated/models';
import DocumentFileUploader from '@/components/forms/inputs/DocumentFileUploader';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import BaseInputNumber from '@/components/forms/inputs/BaseInputNumber.vue';
import Validate from '@/mixins/Validate';
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';
import RepairExtensionsRepository from '@/api/repositories/RepairExtensionsRepository';

export default {
	name: 'OrderDocumentationRepairExtension',
	components: { BaseInputNumber, DocumentFileUploader, BaseInput, BaseCamera, BaseButton, MetaInfoImage, VuePdfEmbed },
	mixins: [LoadingSpinner, Authentication, AppContainerMixin, Validate],
	props: {
		repairExtension: {
			required: true,
		},
		index: {},
		realIndex: {},
		zip_uploaded: { default: false },
		pdf_uploaded: { default: false },
	},
	data() {
		return {
			downloadsCounter: 0,
			enableDownloadImageFiles: false,
			isSomethingChange: false,
			documentPdf: { type: 'reparaturerweiterung_pdf' },
			documentZip: { type: 'reparaturerweiterung_zip' },
			documentData: null,
			drag: false,
			dragElement: null,
			width: 200,
			height: 40,
			signLocations: [],
			selectedSign: null,
			pdfPageCount: 0,
			selectedPage: 1,
			pdfSize: {
				width: 0,
				height: 0,
			},
			pauseEventListener: false,
		};
	},
	created() {
		document.addEventListener('keyup', e => {
			if (e.key === 'Backspace' || e.key === 'Delete') {
				if (!this.pauseEventListener) {
					this.removeSignLocation();
				}
			}
		});
	},
	computed: {
		showCalcButton() {
			return !this.isAppContainer && this.repairExtension.pdf_created_at == null;
		},
		isEditable() {
			return this.repairExtension.id != null && this.isRepairExtensionRequested;
		},
		isExtensionClosed() {
			return !this.repairExtension.isNew && this.isUserAllowedInLevel5or6;
		},
		getSaveButtonText() {
			return !this.repairExtension.isNew ? this.$t('general.save') : this.$t('apply_repair_extension_short');
		},
		getAuthenticatedUserGroupLevel() {
			return this.$store.getters.getAuthenticatedUserGroupLevel;
		},
		isRepairExtensionApproved() {
			return this.repairExtension.status === RepairExtensionStatusEnum.Approved;
		},
		isRepairExtensionRejected() {
			return this.repairExtension.status === RepairExtensionStatusEnum.Rejected;
		},
		isRepairExtensionRequested() {
			return this.repairExtension.status === RepairExtensionStatusEnum.Requested;
		},
	},
	methods: {
		askForPDFOverwrite() {
			if (this.repairExtension.pdf != null) {
				this.$swal({
					title: this.$t('sure').toString(),
					text: this.$t('repair_extension_calculation_file_overwrite').toString(),
					icon: 'warning',
					cancelButtonText: this.$t('general.cancel').toString(),
					showCancelButton: true,
					confirmButtonText: this.$t('overwrite').toString(),
				}).then(result => {
					if (result.value) {
						this.$refs.DocumentFileUploaderPdf.openUpload();
					}
				});
			} else {
				this.$refs.DocumentFileUploaderPdf.openUpload();
			}
		},
		updateDocumentPdf($event) {
			//	this.save();
			this.documentData = $event;
			setTimeout(() => {
				this.pdfPageCount = this.$refs.myPdfComponent.pageCount;
			}, 500);
			this.$swal(this.$t('general.hint').toString(), this.$t('repair_extension_mark_sign_hint').toString(), 'warning');
		},
		checkIsFileSelected() {
			this.enableDownloadImageFiles = undefined;
			this.enableDownloadImageFiles = this.isOneFileSelected(this.repairExtension.imageFiles);
		},
		submitImage(repairExtension, imageFile) {
			return DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesIdRepairextensionsExtidMediafilesPost(
					parseInt(this.$route.params.processId || this.$route.params.id),
					repairExtension.id,
					imageFile,
				)
				.then(result => {
					imageFile.id = result.data.id;
					imageFile.timestamp = result.data.timestamp;
					imageFile.isNotUploaded = false;
				});
		},
		getDownloadURL() {
			const ids = [];
			const array = this.repairExtension.imageFiles;

			for (let i = 0; i < array.length; i++) {
				const file = array[i];

				if (file.id && !file.isNotUploaded && file.isSelected) {
					ids.push('mediaIds[]=' + file.id);
				}
			}

			const date = new Date();
			const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
			const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(date);
			const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
			const dateString = `${year}${month}${day}`;

			const mediaIdQueryString = ids.join('&');
			let url = '';
			new RepairExtensionsRepository()
				.downloadRepairExtensionMediaFiles(parseInt(this.$route.params.processId), this.repairExtension.id, mediaIdQueryString)
				.then(response => {
					const fileName = 'Reparaturerweiterung#' + this.realIndex + '_' + dateString + '.zip';
					const blob = new Blob([response.data]);
					url = window.URL.createObjectURL(blob);
					setTimeout(function() {
						const a = document.createElement('a');
						a.style.display = 'none';
						a.href = url;
						a.download = fileName;
						document.body.appendChild(a);
						a.click();
						window.URL.revokeObjectURL(url);
					}, 200);
				})
				.catch(e => {
					console.log(e);
					this.$swal(this.$t('general.error').toString(), this.$t('media_cannot_be_downloaded').toString(), 'error');
				});
		},
		isOneFileSelected(files) {
			return files.find(element => element.isSelected) !== undefined;
		},
		getStringFromDate(string) {
			return getStringFromDate(new Date(string * 1000));
		},
		getTimeFromDate(string) {
			return getTimeFromDate(new Date(string * 1000));
		},
		submitDeleteMetaFiles() {
			const array = [];
			this.repairExtension.deletedMetaFiles?.forEach((file, index) => {
				if (!file.isNotUploaded) {
					const promise = MetainfofileApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios).metainfofileIdDelete(
						file.id,
						'repairExtension',
					);
					array.push(promise);
				}
			});
			this.repairExtension.deletedMetaFiles = undefined;
			return array;
		},
		addImageFile(image) {
			this.setLoadingStatus(true);
			MetainfofileApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.metainfofileTmpimageuploadPost({ type: 'image', base64Content: image })
				.then(result => {
					this.repairExtension.imageFiles.unshift({
						id: v4(),
						timestamp: result.data.timestamp,
						type: MetaInfoFileTypeEnum.Image,
						base64Content: result.data.base64Content,
						isNotUploaded: true,
					});
					this.isSomethingChange = true;
					this.downloadsCounter++;
					this.setLoadingStatus(false);
				})
				.catch(error => {
					this.setLoadingStatus(false);
					this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
				});
		},
		removeImageFile(index) {
			const file = this.repairExtension.imageFiles.splice(index, 1)[0];
			this.removeMetaFile(file);
		},
		removeMetaFile(file) {
			if (!file.isNotUploaded) {
				if (!this.repairExtension.deletedMetaFiles) {
					this.repairExtension.deletedMetaFiles = [];
				}
				this.repairExtension.deletedMetaFiles.push(file);
			}
			this.downloadsCounter++; // TODO FIND A BETTER WAY TO UPDATE UI
		},
		updateRepairExtension() {
			const tmpRepairExtension = { ...this.repairExtension, imageFiles: [] };
			return DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios).damagecasesIdRepairextensionsExtidPut(
				parseInt(this.$route.params.processId || this.$route.params.id),
				this.repairExtension.id,
				tmpRepairExtension,
			);
		},
		updateImageFiles() {
			const array = [];
			this.repairExtension.imageFiles
				.slice()
				.reverse()
				.forEach((imageFile, index) => {
					if (imageFile.isNotUploaded) {
						array.push(this.submitImage(this.repairExtension, imageFile));
					}
				});
			return array;
		},
		save(emit = true) {
			if (this.repairExtension.isNotUploaded) {
				if (!this.repairExtension.description) {
					this.$swal(this.$t('general.attention').toString(), this.$t('enter_text_in_description').toString(), 'warning');
					return;
				}
				this.setLoadingStatus(true);
				this.repairExtension.date = getUnixTimestamp();
				DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
					.damagecasesIdRepairextensionsPost(parseInt(this.$route.params.processId || this.$route.params.id), this.repairExtension)
					.then(result => {
						this.repairExtension.id = result.data.id;
						this.repairExtension.isNotUploaded = false;
						this.upload();
						if (emit) {
							this.$emit('saveExtension', true);
							this.repairExtension.isNew = false;
						}
					})
					.catch(error => {
						this.setLoadingStatus(false);
						this.$swal(this.$t('general.error').toString(), this.$t('upload_repair_extension_error').toString(), 'error');
					});
			} else {
				this.setLoadingStatus(true);
				this.upload();
				this.$emit('saveExtension', true);
			}
		},
		upload() {
			const array = [];
			array.push(this.updateRepairExtension());
			array.concat(this.updateImageFiles());
			array.concat(this.submitDeleteMetaFiles());
			return Promise.all(array)
				.then(result => {
					this.isSomethingChange = false;
					this.$store.commit('setRepairExtensionNeedUpdate', true);
					this.setLoadingStatus(false);
					this.$swal(this.$t('general.success').toString(), this.$t('order_doc_repair_extension_applied').toString(), 'success');
				})
				.catch(error => {
					this.setLoadingStatus(false);
					this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
				});
		},
		sendSignableDocToApi(event) {
			// constants for the displayed size of the PDF in the Vue.js app
			const displayedPdfWidth = 553; // width of the PDF as it is displayed in the Vue.js app
			const displayedPdfHeight = 780; // height of the PDF as it is displayed in the Vue.js app

			// scaling factors based on the actual size of the PDF and the displayed size
			const scaleX = this.pdfSize.width / displayedPdfWidth;
			const scaleY = this.pdfSize.height / displayedPdfHeight;

			let signatureFieldWidth = 252; // actual width of signature field in pixels
			let signatureFieldHeight = 63; // actual height of signature field in pixels

			// calculate the aspect ratio of the signature field

			const parsedSigns = [];
			this.signLocations.forEach(element => {
				signatureFieldHeight = element.height;
				signatureFieldWidth = element.width;

				const aspectRatio = signatureFieldWidth / signatureFieldHeight;

				// scale the positions and dimensions to match the actual PDF size
				const scaledX = element.x * scaleX;
				const scaledY = element.y * scaleY;
				const scaledWidth = signatureFieldWidth * scaleX; // use the actual width scaled
				const scaledHeight = scaledWidth / aspectRatio; // calculate height based on the scaled width to maintain aspect ratio

				// convert the scaled coordinates to percentages of the PDF dimensions
				const percentX = (scaledX / this.pdfSize.width) * 100;
				const percentY = (scaledY / this.pdfSize.height) * 100;
				const percentWidth = (scaledWidth / this.pdfSize.width) * 100;
				const percentHeight = (scaledHeight / this.pdfSize.height) * 100;

				// calculate the coordinates of the signature field corners
				element.coordinates.a = { x: percentX, y: percentY };
				element.coordinates.b = { x: percentX + percentWidth, y: percentY };
				element.coordinates.c = { x: percentX, y: percentY + percentHeight };
				element.coordinates.d = { x: percentX + percentWidth, y: percentY + percentHeight };

				// add the signature field data to the parsedSigns array
				parsedSigns.push({ coordinates: element.coordinates, page: element.page });
			});

			const uuid = event.uuid;
			const request = {
				signable: {
					base64content: this.documentData,
					document_uuid: uuid,
					signs: parsedSigns,
					document_typ: 'reparaturerweiterung_pdf',
					documentable_type: 'repair_extension',
					documentable_id: parseInt(this.$route.params.processId || this.$route.params.id),
					misc_name: this.$t('repair_extension_index', { index: this.realIndex }),
				},
				repair_extension: { price: this.repairExtension.price },
			};

			new RepairExtensionsRepository()
				.updateOrderDocumentationRepairExtension(parseInt(this.$route.params.processId || this.$route.params.id), this.repairExtension.id, request)
				.then(response => {
					this.$emit('updateDocumentPdf', this.documentData);
					this.$swal(this.$t('general.success').toString(), this.$t('repair_extension_request_sent_to_customer').toString(), 'success');
				})
				.catch(e => {
					this.$swal(this.$t('general.error').toString(), this.$t('repair_extension_request_cannot_be_sent_sent_to_customer').toString(), 'error');
					console.log(e);
				});
		},
		updateExtension: function() {
			if (this.validate()) {
				if (this.documentData) {
					this.$refs.DocumentFileUploaderPdf.triggerFileUpload();
				} else {
					this.$swal(this.$t('general.error').toString(), this.$t('please_upload_document').toString(), 'error');
				}
			} else {
				this.$swal(this.$t('general.error').toString(), this.$t('please_check_input').toString(), 'error');
			}
		},
		pushSignLocation() {
			this.signLocations.push({
				id: this.signLocations.length,
				page: this.selectedPage,
				width: this.width,
				height: this.height,
				x: 0,
				y: 0,
				coordinates: {},
			});
		},
		removeSignLocation() {
			if (this.selectedSign != null) {
				this.signLocations = this.signLocations.filter(element => element.id !== this.selectedSign);
			}
		},
		onDragStop(x, y, id) {
			const element = this.signLocations.find(element => element.id === id);
			if (element != null) {
				element.x = x;
				element.y = y;
			}
		},
		onResize(x, y, width, height, id) {
			const element = this.signLocations.find(element => element.id === id);
			if (element != null) {
				element.height = height;
				element.width = width;
				element.x = x;
				element.y = y;
			}
		},
		getSignsForPage(page) {
			return this.signLocations.filter(element => element.page === page);
		},
		getPdfSize() {
			this.$nextTick(() => {
				const pdfElement = this.$refs.myPdfComponent.$el.querySelector('canvas');
				if (pdfElement) {
					this.pdfSize.width = pdfElement.width;
					this.pdfSize.height = pdfElement.height;
				} else {
					console.error('Unable to find PDF element');
				}
			});
		},
	},
	watch: {
		documentData(newValue, oldValue) {
			if (newValue !== null) {
				//TODO: more advanced approach? change with using mutation observer
				//delay needed to get pdf size (watch gets called before pdf is rendered)
				setTimeout(() => {
					this.getPdfSize();
				}, 1200); // Adjust the delay as needed
			}
		},
	},
};
</script>

<style scoped>
/* Container, in dem NUR die PDF-Seiten scrollen dürfen */
.pdf-scrollbox {
	max-height: 70vh; /* Höhe beliebig anpassen */
	overflow-y: auto; /* eigene Scrollbar */
	overscroll-behavior: contain; /* verhindert Scroll-Bubbling   */
}
</style>
